import SmartAckCombobox from 'components/comboboxes/SmartAckCombobox';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import HubertPopover from 'components/HubertUI/HubertPopover';
import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { updateConversationSettings } from 'rtk/Question.Editor.Slice';
import { selectQuestionInTemplateEditAndUneditable } from 'rtk/questionEditorSelector';
import { useQuestionEditorContext } from '../../context/create-question.context';
import { useTranslation } from 'react-i18next';

type SettingRowProps = {
  id: string;
  label: string;
  description: ReactNode;
  control: ReactNode;
  indent?: boolean;
  muted?: boolean;
};

const SettingRow = ({ id, label, description, control, indent, muted }: SettingRowProps) => (
  <div className={`py-1.5 ${indent ? 'pl-4' : ''}`}>
    <div className={`flex flex-row items-center justify-between gap-4 ${muted ? 'opacity-50' : ''}`}>
      <div className="flex min-w-0 flex-row items-center gap-1.5">
        <HubertLabel htmlFor={id} className="cursor-pointer truncate">
          {label}
        </HubertLabel>
        <HubertPopover>
          <HubertPopover.Trigger>
            <Info className="h-3.5 w-3.5 cursor-pointer text-gray-400 hover:text-gray-600" />
          </HubertPopover.Trigger>
          <HubertPopover.Content className="max-w-[360px] p-3">
            <div className="text-sm leading-relaxed text-gray-700">{description}</div>
          </HubertPopover.Content>
        </HubertPopover>
      </div>
      {control}
    </div>
  </div>
);

const QuestionConversationSettings = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const conversationSettings = useAppSelector((state) => state.questionEditor.byId[questionId].conversation_settings);
  const disabled = useAppSelector((state) => selectQuestionInTemplateEditAndUneditable(state, questionId));

  const detectionActive = conversationSettings?.digression?.detection_active ?? false;
  // Default ON: absent → detection active, matching the engine (languageDetectionActiveFor).
  const languageDetectionActive = conversationSettings?.language_detection?.active ?? true;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-semibold">{t('conversationSettings.heading')}</h3>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="flex flex-col divide-y divide-gray-100">
        <div className="flex flex-row items-center justify-between gap-4 py-1.5">
          <HubertLabel htmlFor="conv-smart-ack" className="cursor-pointer">
            {t('conversationSettings.smartAck')}
          </HubertLabel>
          <div className="w-[260px]">
            <SmartAckCombobox
              disabled={disabled}
              onValueChange={(value) => {
                let smartAckId = value as string;
                if (Array.isArray(value)) {
                  smartAckId = value[0];
                }
                dispatch(
                  updateConversationSettings({
                    questionId,
                    settings: {
                      smart_ack: {
                        id: smartAckId,
                        active: !!smartAckId && smartAckId.length > 0,
                      },
                    },
                  }),
                );
              }}
              value={conversationSettings?.smart_ack?.id ?? undefined}
            />
          </div>
        </div>

        <SettingRow
          id="conv-experience-followup"
          label={t('conversationSettings.experienceFollowup.label')}
          description={t('conversationSettings.experienceFollowup.description')}
          control={
            <HubertSwitch
              id="conv-experience-followup"
              disabled={disabled}
              checked={conversationSettings?.experience_followup?.active ?? false}
              onCheckedChange={(checked) => {
                dispatch(
                  updateConversationSettings({
                    questionId,
                    settings: {
                      experience_followup: { id: 'experience_followup', active: checked },
                    },
                  }),
                );
              }}
            />
          }
        />

        <SettingRow
          id="conv-language-detection"
          label={t('conversationSettings.languageDetection.label')}
          description={t('conversationSettings.languageDetection.description')}
          control={
            <HubertSwitch
              id="conv-language-detection"
              disabled={disabled}
              checked={languageDetectionActive}
              onCheckedChange={(checked) => {
                dispatch(
                  updateConversationSettings({
                    questionId,
                    settings: {
                      language_detection: { active: checked },
                    },
                  }),
                );
              }}
            />
          }
        />
      </div>

      <div className="flex flex-col gap-1">
        <h4 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          {t('conversationSettings.digression.heading')}
        </h4>
        <div className="flex flex-col divide-y divide-gray-100">
          <SettingRow
            id="conv-digression-detection"
            indent
            label={t('conversationSettings.digression.detection.label')}
            description={t('conversationSettings.digression.detection.description')}
            control={
              <HubertSwitch
                id="conv-digression-detection"
                disabled={disabled}
                checked={detectionActive}
                onCheckedChange={(checked) => {
                  dispatch(
                    updateConversationSettings({
                      questionId,
                      settings: {
                        digression: {
                          ...(conversationSettings?.digression ?? {}),
                          detection_active: checked,
                        },
                      },
                    }),
                  );
                }}
              />
            }
          />
          <SettingRow
            id="conv-digression-rephrase"
            indent
            muted={!detectionActive}
            label={t('conversationSettings.digression.rephrase.label')}
            description={
              <>
                {t('conversationSettings.digression.rephrase.description')}
                <br />
                <br />
                <strong>{t('conversationSettings.note')}</strong> {t('conversationSettings.requiresDetection')}
              </>
            }
            control={
              <HubertSwitch
                id="conv-digression-rephrase"
                disabled={disabled || !detectionActive}
                checked={conversationSettings?.digression?.followup_rephrase_active ?? false}
                onCheckedChange={(checked) => {
                  dispatch(
                    updateConversationSettings({
                      questionId,
                      settings: {
                        digression: {
                          ...(conversationSettings?.digression ?? {}),
                          followup_rephrase_active: checked,
                        },
                      },
                    }),
                  );
                }}
              />
            }
          />
          <SettingRow
            id="conv-digression-intent-fallback"
            indent
            muted={!detectionActive}
            label={t('conversationSettings.digression.intentFallback.label')}
            description={
              <>
                {t('conversationSettings.digression.intentFallback.description')}
                <br />
                <br />
                <strong>{t('conversationSettings.note')}</strong> {t('conversationSettings.requiresDetection')}
              </>
            }
            control={
              <HubertSwitch
                id="conv-digression-intent-fallback"
                disabled={disabled || !detectionActive}
                checked={conversationSettings?.digression?.intent_llm_fallback_active ?? false}
                onCheckedChange={(checked) => {
                  dispatch(
                    updateConversationSettings({
                      questionId,
                      settings: {
                        digression: {
                          ...(conversationSettings?.digression ?? {}),
                          intent_llm_fallback_active: checked,
                        },
                      },
                    }),
                  );
                }}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export default QuestionConversationSettings;
